import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapLocation } from '../models/MapModels';
import { toApiBoundsStrings } from '../components/map/mapViewportUtils';
import { getRadiusBounds } from '../components/map/initialDiscoveryViewport';

export enum MapMode {
  INITIAL_LOAD = 'INITIAL_LOAD',
  USER_VIEWPORT_MODE = 'USER_VIEWPORT_MODE',
}

export interface ViewportBounds {
  northEast: { lat: number; lng: number };
  southWest: { lat: number; lng: number };
}

const INITIAL_ZOOM_WIDE_AREA = 9;
const VIEWPORT_FETCH_DEBOUNCE_MS = 1000;
const MAP_CENTER_DECIMAL_PLACES = 6;

export interface DiscoveryViewport {
  center: MapLocation;
  topLeftLocation: string;
  bottomRightLocation: string;
}

interface UseMapViewportParams {
  effectiveLocation?: MapLocation;
  fallBackLocation: MapLocation;
  userLocation?: MapLocation;
  waitingForBrowserLocation?: boolean;
  hasGpsFix?: boolean;
  preSelectedBranchId?: string;
}

interface UseMapViewportResult {
  discoveryViewport?: DiscoveryViewport;
  handleViewportChange: (top: number, bottom: number, left: number, right: number, center: MapLocation) => void;
  mapZoom: number;
  setMapZoom: (z: number) => void;
  mapMode: MapMode;
  setMapMode: (m: MapMode) => void;
  autoZoomOnFirstLoad: boolean;
  setAutoZoomOnFirstLoad: (v: boolean) => void;
  hasCenteredOnEffectiveLocation: boolean;
  lastMissionsViewCenter?: MapLocation;
  lastMissionsViewZoom?: number;
  setLastMissionsViewCenter: (loc: MapLocation | undefined) => void;
  setLastMissionsViewZoom: (z: number | undefined) => void;
  viewportCommand?: {
    id: number;
    type: 'fitBounds' | 'centerZoom';
    center?: MapLocation;
    zoom?: number;
    northEast?: MapLocation;
    southWest?: MapLocation;
    padding?: number;
    edgePadding?: { top: number; right: number; bottom: number; left: number };
    minZoom?: number;
    maxZoom?: number;
    suppressBoundsCallback?: boolean;
  };
  setViewportCommand: (cmd: UseMapViewportResult['viewportCommand']) => void;
  activeBounds?: ViewportBounds;
  setActiveBounds: (b: ViewportBounds | undefined) => void;
  topLeftLocation?: string;
  bottomRightLocation?: string;
  setTopLeftLocation: (v: string | undefined) => void;
  setBottomRightLocation: (v: string | undefined) => void;
  appliedTopLeftLocation?: string;
  appliedBottomRightLocation?: string;
  setAppliedTopLeftLocation: (v: string | undefined) => void;
  setAppliedBottomRightLocation: (v: string | undefined) => void;
  showNoMissionOverlay: boolean;
  setShowNoMissionOverlay: (v: boolean) => void;
  suppressSearchAreaDuringInitialZoom: boolean;
  setSuppressSearchAreaDuringInitialZoom: (v: boolean) => void;
  hasViewportBounds: boolean;
  hasCenteredOnEffectiveLocationRef: React.MutableRefObject<boolean>;
  userHasPannedSinceProgrammaticMoveRef: React.MutableRefObject<boolean>;
  applyNextProgrammaticBoundsRef: React.MutableRefObject<boolean>;
  ignoreNextBoundsFromClusterRef: React.MutableRefObject<boolean>;
  restoreViewAfterMapReadyRef: React.MutableRefObject<boolean>;
  ignoreNextBoundsFromRestoreRef: React.MutableRefObject<boolean>;
  handleBoundsChange: (top: number, bottom: number, left: number, right: number) => void;
  cancelPendingViewportSearch: () => void;
  resetMapToInitialLoad: (center?: MapLocation) => void;
}

export const useMapViewport = ({
  effectiveLocation,
  fallBackLocation,
  userLocation,
  waitingForBrowserLocation,
  hasGpsFix,
  preSelectedBranchId,
}: UseMapViewportParams): UseMapViewportResult => {
  const [mapZoom, setMapZoom] = useState(INITIAL_ZOOM_WIDE_AREA);
  const [discoveryViewport, setDiscoveryViewport] = useState<DiscoveryViewport>();
  const viewportTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [autoZoomOnFirstLoad, setAutoZoomOnFirstLoad] = useState(false);
  const [mapMode, setMapMode] = useState<MapMode>(MapMode.INITIAL_LOAD);
  const mapModeRef = useRef(mapMode);
  mapModeRef.current = mapMode;
  const [viewportCommand, setViewportCommand] = useState<UseMapViewportResult['viewportCommand']>();
  const [showNoMissionOverlay, setShowNoMissionOverlay] = useState(false);
  const [suppressSearchAreaDuringInitialZoom, setSuppressSearchAreaDuringInitialZoom] = useState(false);
  const [hasCenteredOnEffectiveLocation, setHasCenteredOnEffectiveLocation] = useState(false);
  const [lastMissionsViewCenter, setLastMissionsViewCenter] = useState<MapLocation | undefined>(undefined);
  const [lastMissionsViewZoom, setLastMissionsViewZoom] = useState<number | undefined>(undefined);
  const [activeBounds, setActiveBounds] = useState<ViewportBounds | undefined>(undefined);
  const [topLeftLocation, setTopLeftLocation] = useState<string | undefined>(undefined);
  const [bottomRightLocation, setBottomRightLocation] = useState<string | undefined>(undefined);
  const [appliedTopLeftLocation, setAppliedTopLeftLocation] = useState<string | undefined>(undefined);
  const [appliedBottomRightLocation, setAppliedBottomRightLocation] = useState<string | undefined>(undefined);

  const hasCenteredOnEffectiveLocationRef = useRef(false);
  const hasCenteredOnGpsFixRef = useRef(false);
  const userHasPannedSinceProgrammaticMoveRef = useRef(false);
  const applyNextProgrammaticBoundsRef = useRef(false);
  const ignoreNextBoundsFromClusterRef = useRef(false);
  const restoreViewAfterMapReadyRef = useRef(false);
  const ignoreNextBoundsFromRestoreRef = useRef(false);
  const boundsDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingTopLeftRef = useRef<string | undefined>(undefined);
  const pendingBottomRightRef = useRef<string | undefined>(undefined);
  const currentTopLeftRef = useRef<string | undefined>(undefined);
  const currentBottomRightRef = useRef<string | undefined>(undefined);
  const currentAppliedTopLeftRef = useRef<string | undefined>(undefined);
  const currentAppliedBottomRightRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    return () => {
      if (viewportTimerRef.current) {
        clearTimeout(viewportTimerRef.current);
      }
      if (boundsDebounceTimerRef.current) {
        clearTimeout(boundsDebounceTimerRef.current);
        boundsDebounceTimerRef.current = null;
      }
    };
  }, []);
  useEffect(() => {
    currentTopLeftRef.current = topLeftLocation;
  }, [topLeftLocation]);

  useEffect(() => {
    currentBottomRightRef.current = bottomRightLocation;
  }, [bottomRightLocation]);

  useEffect(() => {
    currentAppliedTopLeftRef.current = appliedTopLeftLocation;
  }, [appliedTopLeftLocation]);

  useEffect(() => {
    currentAppliedBottomRightRef.current = appliedBottomRightLocation;
  }, [appliedBottomRightLocation]);

  // Center on an initial target location, then recenter once when the first real GPS fix arrives.
  useEffect(() => {
    if (waitingForBrowserLocation || preSelectedBranchId) {
      return;
    }

    const gpsLocationAvailable =
      !!hasGpsFix &&
      !!userLocation &&
      Number.isFinite(userLocation.lat) &&
      Number.isFinite(userLocation.lng);

    const shouldCenterOnInitialLocation = mapMode === MapMode.INITIAL_LOAD && !hasCenteredOnEffectiveLocationRef.current;
    const shouldRecenterOnFirstGpsFix = gpsLocationAvailable && !hasCenteredOnGpsFixRef.current && !userHasPannedSinceProgrammaticMoveRef.current;

    if (!shouldCenterOnInitialLocation && !shouldRecenterOnFirstGpsFix) {
      return;
    }

    const targetLocation = gpsLocationAvailable ? userLocation : effectiveLocation;

    if (!targetLocation) {
      return;
    }

    hasCenteredOnEffectiveLocationRef.current = true;
    setHasCenteredOnEffectiveLocation(true);

    if (gpsLocationAvailable) {
      hasCenteredOnGpsFixRef.current = true;
    }

    userHasPannedSinceProgrammaticMoveRef.current = false;
    setViewportCommand({
      id: Date.now(),
      type: 'fitBounds',
      ...getRadiusBounds(targetLocation),
      padding: 48,
    });
  }, [waitingForBrowserLocation, hasGpsFix, userLocation, effectiveLocation, mapMode, preSelectedBranchId]);

  const cancelPendingViewportSearch = useCallback(() => {
    if (viewportTimerRef.current) {
      clearTimeout(viewportTimerRef.current);
      viewportTimerRef.current = null;
    }
    if (boundsDebounceTimerRef.current) {
      clearTimeout(boundsDebounceTimerRef.current);
      boundsDebounceTimerRef.current = null;
    }
    // Let the next settled bounds restart the full delay, even within the same rounded area.
    pendingTopLeftRef.current = undefined;
    pendingBottomRightRef.current = undefined;
  }, []);

  const handleViewportChange = useCallback(
    (top: number, bottom: number, left: number, right: number, center: MapLocation) => {
      if (![top, bottom, left, right, center?.lat, center?.lng].every(Number.isFinite)) {
        return;
      }

      const bounds = toApiBoundsStrings({
        northEast: { lat: top, lng: right },
        southWest: { lat: bottom, lng: left },
      });
      const next: DiscoveryViewport = {
        center: {
          lat: Number(center.lat.toFixed(MAP_CENTER_DECIMAL_PLACES)),
          lng: Number(center.lng.toFixed(MAP_CENTER_DECIMAL_PLACES)),
        },
        topLeftLocation: bounds.topLeft,
        bottomRightLocation: bounds.bottomRight,
      };

      if (viewportTimerRef.current) {
        clearTimeout(viewportTimerRef.current);
      }
      viewportTimerRef.current = setTimeout(() => {
        viewportTimerRef.current = null;
        setDiscoveryViewport((previous) => {
          if (
            previous?.center.lat === next.center.lat &&
            previous?.center.lng === next.center.lng &&
            previous?.topLeftLocation === next.topLeftLocation &&
            previous?.bottomRightLocation === next.bottomRightLocation
          ) {
            return previous;
          }
          return next;
        });
      }, VIEWPORT_FETCH_DEBOUNCE_MS);
    },
    [],
  );

  const handleBoundsChange = useCallback((top: number, bottom: number, left: number, right: number) => {
    if (
      typeof top !== 'number' ||
      !Number.isFinite(top) ||
      typeof bottom !== 'number' ||
      !Number.isFinite(bottom) ||
      typeof left !== 'number' ||
      !Number.isFinite(left) ||
      typeof right !== 'number' ||
      !Number.isFinite(right)
    ) {
      return;
    }

    const nextBounds: ViewportBounds = {
      northEast: { lat: top, lng: right },
      southWest: { lat: bottom, lng: left },
    };
    const apiBounds = toApiBoundsStrings(nextBounds);
    const nextTopLeft = apiBounds.topLeft;
    const nextBottomRight = apiBounds.bottomRight;

    setActiveBounds((prev) => {
      if (
        prev &&
        prev.northEast.lat === nextBounds.northEast.lat &&
        prev.northEast.lng === nextBounds.northEast.lng &&
        prev.southWest.lat === nextBounds.southWest.lat &&
        prev.southWest.lng === nextBounds.southWest.lng
      ) {
        return prev;
      }
      return nextBounds;
    });

    // Remember the visible area even when this move should not fetch missions.
    // The mobile web map needs these bounds when returning from the list.
    if (ignoreNextBoundsFromClusterRef.current) {
      ignoreNextBoundsFromClusterRef.current = false;
      return;
    }
    if (ignoreNextBoundsFromRestoreRef.current) {
      ignoreNextBoundsFromRestoreRef.current = false;
      return;
    }

    if (nextTopLeft === pendingTopLeftRef.current && nextBottomRight === pendingBottomRightRef.current) {
      return;
    }
    pendingTopLeftRef.current = nextTopLeft;
    pendingBottomRightRef.current = nextBottomRight;

    if (boundsDebounceTimerRef.current) {
      clearTimeout(boundsDebounceTimerRef.current);
    }
    boundsDebounceTimerRef.current = setTimeout(() => {
      boundsDebounceTimerRef.current = null;
      const finalTopLeft = pendingTopLeftRef.current;
      const finalBottomRight = pendingBottomRightRef.current;

      if (currentTopLeftRef.current !== finalTopLeft) {
        currentTopLeftRef.current = finalTopLeft;
        setTopLeftLocation(finalTopLeft);
      }
      if (currentBottomRightRef.current !== finalBottomRight) {
        currentBottomRightRef.current = finalBottomRight;
        setBottomRightLocation(finalBottomRight);
      }

      const shouldApplyViewportBounds =
        userHasPannedSinceProgrammaticMoveRef.current ||
        (mapModeRef.current === MapMode.USER_VIEWPORT_MODE && applyNextProgrammaticBoundsRef.current);

      if (!shouldApplyViewportBounds) {
        return;
      }

      const shouldClearProgrammaticBoundsFlag = applyNextProgrammaticBoundsRef.current && !userHasPannedSinceProgrammaticMoveRef.current;

      if (currentAppliedTopLeftRef.current !== finalTopLeft) {
        currentAppliedTopLeftRef.current = finalTopLeft;
        setAppliedTopLeftLocation(finalTopLeft);
      }
      if (currentAppliedBottomRightRef.current !== finalBottomRight) {
        currentAppliedBottomRightRef.current = finalBottomRight;
        setAppliedBottomRightLocation(finalBottomRight);
      }

      if (shouldClearProgrammaticBoundsFlag) {
        applyNextProgrammaticBoundsRef.current = false;
      }
    }, VIEWPORT_FETCH_DEBOUNCE_MS);
  }, []);
  const hasViewportBounds = useMemo(
    () => !!appliedTopLeftLocation && !!appliedBottomRightLocation,
    [appliedTopLeftLocation, appliedBottomRightLocation],
  );

  // Clear "no missions" overlay when user moves the map.
  useEffect(() => {
    if (!userHasPannedSinceProgrammaticMoveRef.current || mapMode !== MapMode.USER_VIEWPORT_MODE) {
      return;
    }
    const userMoved = activeBounds
      ? Math.abs(activeBounds.northEast.lat - activeBounds.southWest.lat) > 0 ||
      Math.abs(activeBounds.northEast.lng - activeBounds.southWest.lng) > 0
      : false;
    if (userMoved) {
      setShowNoMissionOverlay(false);
    }
  }, [activeBounds, mapMode]);

  // Suppress "search this area" CTA during initial auto-zoom until bounds settle.
  useEffect(() => {
    if (!suppressSearchAreaDuringInitialZoom) {
      return;
    }

    if (autoZoomOnFirstLoad) {
      return;
    }

    const boundsSettled =
      !!topLeftLocation && !!bottomRightLocation && topLeftLocation === appliedTopLeftLocation && bottomRightLocation === appliedBottomRightLocation;

    if (boundsSettled) {
      setSuppressSearchAreaDuringInitialZoom(false);
    }
  }, [suppressSearchAreaDuringInitialZoom, autoZoomOnFirstLoad, topLeftLocation, bottomRightLocation, appliedTopLeftLocation, appliedBottomRightLocation]);

  const resetMapToInitialLoad = (center?: MapLocation) => {
    if (viewportTimerRef.current) {
      clearTimeout(viewportTimerRef.current);
    }
    viewportTimerRef.current = null;
    setDiscoveryViewport(undefined);
    hasCenteredOnEffectiveLocationRef.current = false;
    hasCenteredOnGpsFixRef.current = false;
    userHasPannedSinceProgrammaticMoveRef.current = false;
    applyNextProgrammaticBoundsRef.current = false;
    setHasCenteredOnEffectiveLocation(false);
    setMapMode(MapMode.INITIAL_LOAD);
    setAutoZoomOnFirstLoad(true);
    if (boundsDebounceTimerRef.current) {
      clearTimeout(boundsDebounceTimerRef.current);
      boundsDebounceTimerRef.current = null;
    }
    pendingTopLeftRef.current = undefined;
    pendingBottomRightRef.current = undefined;
    currentTopLeftRef.current = undefined;
    currentBottomRightRef.current = undefined;
    currentAppliedTopLeftRef.current = undefined;
    currentAppliedBottomRightRef.current = undefined;
    setTopLeftLocation(undefined);
    setBottomRightLocation(undefined);
    setAppliedTopLeftLocation(undefined);
    setAppliedBottomRightLocation(undefined);
    setShowNoMissionOverlay(false);
    if (center) {
      setViewportCommand({
        id: Date.now(),
        type: 'fitBounds',
        ...getRadiusBounds(center),
        padding: 48,
      });
    }
  };

  return {
    discoveryViewport,
    handleViewportChange,
    mapZoom,
    setMapZoom,
    mapMode,
    setMapMode,
    autoZoomOnFirstLoad,
    setAutoZoomOnFirstLoad,
    hasCenteredOnEffectiveLocation,
    lastMissionsViewCenter,
    lastMissionsViewZoom,
    setLastMissionsViewCenter,
    setLastMissionsViewZoom,
    viewportCommand,
    setViewportCommand,
    activeBounds,
    setActiveBounds,
    topLeftLocation,
    bottomRightLocation,
    setTopLeftLocation,
    setBottomRightLocation,
    appliedTopLeftLocation,
    appliedBottomRightLocation,
    setAppliedTopLeftLocation,
    setAppliedBottomRightLocation,
    showNoMissionOverlay,
    setShowNoMissionOverlay,
    suppressSearchAreaDuringInitialZoom,
    setSuppressSearchAreaDuringInitialZoom,
    hasViewportBounds,
    hasCenteredOnEffectiveLocationRef,
    userHasPannedSinceProgrammaticMoveRef,
    applyNextProgrammaticBoundsRef,
    ignoreNextBoundsFromClusterRef,
    restoreViewAfterMapReadyRef,
    ignoreNextBoundsFromRestoreRef,
    handleBoundsChange,
    cancelPendingViewportSearch,
    resetMapToInitialLoad,
  };
};

export default useMapViewport;
